import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";
import { Alert } from "@mui/material";

export interface DeleteModalProps {
	open: boolean;
	onClose: () => void;
	onDelete: () => void;
	title: string;
	description: string;
	showWarning?: boolean;
	warningText?: string;
	onAdditionalAction?: () => void;
	additionalActionText?: string;
	deleteText?: string;
}

export function DeleteModal({
	open,
	onClose,
	onDelete,
	onAdditionalAction,
	title = "Delete",
	description = "Are you sure you want to delete this item?",
	showWarning = true,
	warningText = "This action cannot be undone.",
	additionalActionText = "Deactivate",
  deleteText = "Delete",
}: DeleteModalProps): React.JSX.Element {
	const [isDeleting, setIsDeleting] = React.useState(false);

	const handleDelete = async () => {
		setIsDeleting(true);

		try {
			await onDelete();
		} catch (error) {
			console.log(error);
		} finally {
			onClose();
			setIsDeleting(false);
		}
	};

	const handleAdditionalAction = async () => {
		setIsDeleting(true);

		try {
			await onAdditionalAction?.();
		} catch (error) {
			console.log(error);
		} finally {
			onClose();
			setIsDeleting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent sx={{ p: 3, minWidth: 440 }}>
				<Stack direction="row" spacing={2} sx={{ display: "flex" }}>
					<Avatar sx={{ bgcolor: "var(--mui-palette-error-50)", color: "var(--mui-palette-error-main)" }}>
						<WarningIcon fontSize="var(--Icon-fontSize)" />
					</Avatar>
					<Stack spacing={3}>
						<Stack spacing={1}>
							<Typography variant="h5">{title}</Typography>
							<Typography color="text.secondary" variant="body2">
								<span dangerouslySetInnerHTML={{ __html: description || "" }} />
								{showWarning && (
									<Alert
										severity="warning"
										sx={{
											mt: 2,
											border: "1px solid",
											borderColor: "warning.main",
											borderRadius: 1,
											'& .MuiAlert-icon': {
												display: 'none',
											},
										}}
									>
										<Typography
                      color="warning.dark"
											variant="body2"
											fontWeight={700}
											sx={{ mb: 0.5, fontSize: "16px" }}
										>
											Warning:
										</Typography>
										<Typography
											variant="body2"
											dangerouslySetInnerHTML={{ __html: warningText || "" }}
										></Typography>
									</Alert>
								)}
							</Typography>
						</Stack>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ p: 3, pt: 0 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={onClose}>
						Cancel
					</Button>
					{onAdditionalAction && (
						<Button color="warning" variant="outlined" onClick={handleAdditionalAction} disabled={isDeleting}>
							{additionalActionText}
						</Button>
					)}
					<Button color="error" variant="contained" onClick={handleDelete} disabled={isDeleting}>
						{deleteText}
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
